((window) => {
  // Line item property keys used to group a bundle's cart items together (see
  // snippets/cart-drawer-bundle.liquid). Checked in this order when an existing item
  // already carries one of them.
  const BUNDLE_ID_PROPERTY_KEYS = ["rbId", "_rc_bundle", "_rb_id"];

  class RechargeWidget extends HTMLElement {
    constructor() {
      super();

      this.showWidgetHandler = this.showWidget.bind(this);
      this.submitHandler = this.submit.bind(this);
      this.disabledWidgetHandler = this.disabledWidget.bind(this);
      this.setBundleItemsHandler = this.setBundleItems.bind(this);

      this.enableButtonHandler = this.enableButton.bind(this);
      this.disableButtonHandler = this.disableButton.bind(this);

      this.subscriptionPerks = this.getAttribute("data-perks").split("|") || [];
      this.currencyFormatter = null;

      // in recharge it will always be 1, if it's a subscription
      this.quantity = 1;
      this.data = null;
      this.variant = null;
      this.product_handle = null;

      this.productForm = null;
      this.form = null;

      this.bundle_items = [];
      this.priceObserver = null;
      this.priceObserverRaf = null;
      this.priceObserverTarget = null;

      this.selected_plan_index = 0;
      this.selected_option = 0;
      this.isSyncingFrequency = false;

      this.cart =
        document.querySelector("cart-notification") ||
        document.querySelector("cart-drawer");
    }

    connectedCallback() {
      document.addEventListener(
        "Recharge:Widget:Enable",
        this.showWidgetHandler,
      );
      document.addEventListener(
        "Recharge:Widget:Disabled",
        this.disabledWidgetHandler,
      );
      document.addEventListener(
        "Recharge:Widget:BundleItems",
        this.setBundleItemsHandler,
      );

      document.addEventListener(
        "Recharge:Widget:BundleMaxDisable",
        this.disableButtonHandler,
      );
      document.addEventListener(
        "Recharge:Widget:BundleMaxEnable",
        this.enableButtonHandler,
      );
      document.addEventListener(
        "Recharge:Widget:BundleInvalid",
        this.disableButtonHandler,
      );
      document.addEventListener(
        "Recharge:Widget:BundleValid",
        this.enableButtonHandler,
      );

      this.productForm = document.querySelector("product-form");
      if (this.productForm) {
        this.form = this.productForm.querySelector('form[action="/cart/add"]');
        if (this.form) {
          this.form.setAttribute("data-recharge-widget", true);
          this.form.addEventListener("submit", this.submitHandler, false);
        }
      }

      this.startPriceObserver();
      this.formatMainPdpPriceFromDataAttr();
    }

    disconnectedCallback() {
      document.removeEventListener(
        "Recharge:Widget:Enable",
        this.showWidgetHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:Disabled",
        this.disabledWidgetHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:BundleItems",
        this.setBundleItemsHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:BundleMaxDisable",
        this.disableButtonHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:BundleMaxEnable",
        this.enableButtonHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:BundleInvalid",
        this.disableButtonHandler,
      );
      document.removeEventListener(
        "Recharge:Widget:BundleValid",
        this.enableButtonHandler,
      );

      if (this.priceObserver) {
        this.priceObserver.disconnect();
        this.priceObserver = null;
      }
      this.priceObserverTarget = null;
      if (this.priceObserverRaf) {
        cancelAnimationFrame(this.priceObserverRaf);
        this.priceObserverRaf = null;
      }
    }

    parseCurrencyDataAmount(value) {
      const raw = String(value ?? "").trim();
      if (!raw || raw.toLowerCase().includes("bundle")) return null;
      const num = Number(raw);
      if (!Number.isFinite(num)) return null;
      if (Math.abs(num) >= 100) return num / 100;
      return num;
    }

    formatMainPdpPriceFromDataAttr() {
      const priceContainer = document.querySelector('[id^="price-"]');
      if (!priceContainer) return;

      priceContainer
        .querySelectorAll(".price-item[data-currency-price]")
        .forEach((node) => {
          const amount = this.parseCurrencyDataAmount(
            node.getAttribute("data-currency-price"),
          );
          if (amount == null) return;
          node.textContent = this.priceFormatter(amount);
        });
    }

    startPriceObserver() {
      const priceContainer = document.querySelector('[id^="price-"]');
      if (!priceContainer) return;

      if (this.priceObserver && this.priceObserverTarget === priceContainer) {
        return;
      }

      if (this.priceObserver) {
        this.priceObserver.disconnect();
      }

      this.priceObserver = new MutationObserver(() => {
        if (this.priceObserverRaf) return;
        this.priceObserverRaf = requestAnimationFrame(() => {
          this.priceObserverRaf = null;
          this.formatMainPdpPriceFromDataAttr();
        });
      });

      this.priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true,
      });

      this.priceObserverTarget = priceContainer;
    }

    disableButton() {
      let button = this.productForm.querySelector('button[type="submit"]');
      if (button) {
        button.disabled = true;
      }
    }

    /**
     * Function to calculate the discount percentage based on the total quantity of items.
     *
     * @param {Array} items - The array of items, each item is an object with a quantity property.
     * @returns {number} The discount percentage.
     */
    getDiscountPercentage(items) {
      // Calculate the total quantity of items
      let count = items.reduce((acc, item) => acc + item.quantity, 0);

      // Check if the total quantity is between 2 and 5 (inclusive)
      if (count >= 2 && count <= 5) {
        // If true, return a discount of 10%
        return 10;
      }
      // Check if the total quantity is 6 or more
      else if (count >= 6) {
        // If true, return a discount of 15%
        return 15;
      } else {
        // If the total quantity is less than 2, return no discount
        return 0;
      }
    }

    enableButton() {
      let button = this.productForm.querySelector('button[type="submit"]');
      if (button) {
        button.disabled = false;
      }
    }

    disabledWidget() {
      this.setAttribute("data-loading", true);
      // do not disable the widget if the template is recharge-bundle-accordions
      if (this.closest("product-info")) {
        if (this.closest("product-info").querySelector("cstmbundle-builder")) {
          this.setAttribute("data-loading", false);
          return;
        }
      }

      this.productForm = document.querySelector("product-form");
      if (this.productForm) {
        this.form = this.productForm.querySelector('form[action="/cart/add"]');
        if (this.form) {
          this.form.removeAttribute("data-recharge-widget");
          this.form.removeEventListener("submit", this.submitHandler, false);
        }
      }
    }

    /* Returns the one-time price for the current variant in the active
       market's currency, already correctly localized by Shopify (fixed
       local pricing or auto-converted) - sourced from Liquid via
       data-base-price-cents on this element, set in
       snippets/subscription_widget.liquid. Recharge's own unit_price /
       discounted_price fields are raw shop-base-currency numbers with no
       awareness of Shopify Markets pricing, so they must not be used for
       display; only their (currency-agnostic) discount percentage is. */
    getLocalizedBasePrice() {
      const cents = parseFloat(this.getAttribute("data-base-price-cents"));
      return Number.isFinite(cents) ? cents / 100 : 0;
    }

    getSubscribePrice(sellingPlan) {
      const basePrice = this.getLocalizedBasePrice();
      const pct = Number(sellingPlan?.plan?.price_adjustments_value) || 0;
      return basePrice * (1 - pct / 100);
    }

    priceFormatter(price) {
      const locale =
        document.documentElement.lang ||
        this.getAttribute("data-locale") ||
        navigator.language ||
        undefined;
      const activeCurrency =
        window?.Shopify?.currency?.active ||
        window?.Shopify?.currency?.currentCurrency ||
        this.getAttribute("data-currency-code") ||
        "USD";

      let normalizedPrice = Number(price);
      if (!Number.isFinite(normalizedPrice)) {
        normalizedPrice = Number(
          String(price ?? "")
            .replace(/[^0-9.,-]/g, "")
            .replace(/,/g, ""),
        );
      }
      if (!Number.isFinite(normalizedPrice)) {
        normalizedPrice = 0;
      }
      /* No rate/currency conversion here: callers now always pass an
         already-correctly-localized amount (see getLocalizedBasePrice() /
         getSubscribePrice() above). This function only formats it. */

      try {
        const resolvedOptions = this.currencyFormatter?.resolvedOptions?.() || {};
        const formatterCurrency = resolvedOptions.currency;
        const formatterLocale = resolvedOptions.locale;
        if (!this.currencyFormatter || formatterCurrency !== activeCurrency || formatterLocale !== locale) {
          this.currencyFormatter = new Intl.NumberFormat(locale, {
            style: "currency",
            currency: activeCurrency,
          });
        }
      } catch (_e) {
        this.currencyFormatter = new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        });
      }

      return this.currencyFormatter.format(normalizedPrice);
    }

    toCents(price) {
      return Math.round((Number(price) || 0) * 100);
    }

    showWidget(event) {
      this.setAttribute("data-loading", true);
      let detail = event.detail;

      /* change the widget message */
      detail.data.widget_settings.subscribe_message =
        "Subscribe and unlock 10% savings";

      let templateElement = this.querySelector("#subscription_widget_template");
      let template = templateElement.content.cloneNode(true);

      this.data = detail.data;
      this.variant = detail.variant;
      this.product_handle = detail.product_handle;

      let headingElement = template.querySelector(
        ".subscription-details .heading",
      );
      let priceElement = template.querySelector(".subscription-price");
      let selectElement = template.querySelector(
        ".delivery-frequency-dropdown select",
      );
      let button = this.productForm.querySelector('button[type="submit"]');
      let bundlelink = this.productForm.querySelector(
        ".recharge-bundle-product",
      );

      /* Keep every delivery-frequency option in the dropdown. Recharge often
         attaches all product plans to each variant; Shopify only accepts the
         plan allocated to the selected variant, so we sync the selected value
         (and matching Option radios) instead of removing choices. */
      const variantSellingPlans = detail.variant.selling_plans || [];

      /* Populate the frequency dropdown before any checkbox auto-click below,
         so the initial sync to the main PDP price can resolve a selling plan. */
      selectElement.innerHTML = variantSellingPlans
        .map((item) => {
          return `<option value="${item.selling_plan_id}">${item.plan.selling_plan_name}</option>`;
        })
        .join("");

      if (variantSellingPlans.length <= 1) {
        this.style.display = "none";
      } else {
        this.style.display = "";
      }
      if (this.isOneTimeOnlyProduct()) {
        this.disabledWidget();
        return;
      }

      if (detail.data.product.is_subscription_only) {
        template.querySelector(".widget__subscription--onetime").remove();

        template
          .querySelector(".widget_checkbox")
          .querySelector('[type="checkbox"]').checked = true;
        template
          .querySelector(".widget_checkbox")
          .querySelector('[type="checkbox"]').disabled = true;
        template
          .querySelector(".widget_checkbox")
          .querySelector('[type="checkbox"]').readOnly = true;
        template
          .querySelector(".widget_checkbox")
          .querySelector(".checkmark").style.display = "none";

        headingElement.innerHTML =
          detail.data.widget_settings.subscribe_message;
        if (headingElement.closest(".subscription-details")) {
          if (
            !headingElement
              .closest(".subscription-details")
              .querySelector(".widget-perk__tool-wrapper")
          ) {
            headingElement
              .closest(".subscription-details")
              .querySelector(".subscription-details .subtext")
              .insertAdjacentHTML(
                "afterend",
                `              <div class="widget-perk__tool-wrapper">
                <div class="widget-perk__tooltip-title">Subscription Perks</div>
                <div class="widget-perk__tooltip-content">
                  <ul>
                    ${this.subscriptionPerks.map((perk) => `<li>${perk.trim()}</li>`).join("")}
                  </ul>
                </div>
              </div>`,
              );
          }
        }

        if (variantSellingPlans.length <= 1) {
          template.firstElementChild.style.display = "none";
          if (this.productForm) {
            if (button && !bundlelink) {
              button.classList.add("secondary-button");
            }
            button.querySelector("span").innerHTML =
              detail.data.widget_settings.subscribe_without_discount_message;
          }
        } else {
          if (this.productForm) {
            button.querySelector("span").innerHTML =
              detail.data.widget_settings.subscribe_without_discount_message;
          }
        }
      }

      if (!detail.data.product.is_subscription_only) {
        headingElement.innerHTML =
          detail.data.widget_settings.subscribe_message;
        if (headingElement.closest(".subscription-details")) {
          if (
            !headingElement
              .closest(".subscription-details")
              .querySelector(".widget-perk__tool-wrapper")
          ) {
            headingElement
              .closest(".subscription-details")
              .querySelector(".subscription-details .subtext")
              .insertAdjacentHTML(
                "afterend",
                `              <div class="widget-perk__tool-wrapper">
                <div class="widget-perk__tooltip-title">Subscription Perks</div>
                <div class="widget-perk__tooltip-content">
                  <ul>
                    ${this.subscriptionPerks
                      .map((perk) => `<li>${perk.trim()}</li>`)
                      .join("")}
                  </ul>
                </div>
              </div>`,
              );
          }
        }
        template.querySelector(
          ".widget__subscription--onetime .subscription-price",
        ).innerHTML = this.priceFormatter(this.getLocalizedBasePrice());
        [
          ...template.querySelectorAll(
            '.widget_checkbox input[type="checkbox"]',
          ),
        ].forEach((el, elidx, list) => {
          el.addEventListener("click", (event) => {
            let subscriptionChangeEvent;

            if (el.checked === false) {
              event.preventDefault();
            } else {
              this.selected_option = elidx;
              list.forEach((item) => {
                if (item != el) {
                  item.checked = false;
                }
              });
            }
            if (
              el.parentNode.parentNode.classList.contains(
                "widget__subscription--onetime",
              )
            ) {
              subscriptionChangeEvent = new CustomEvent(
                "Recharge:Widget:SubscriptionChange",
                {
                  detail: {
                    priceEl: el
                      .closest(".widget_checkbox")
                      ?.querySelector(".subscription-price"),
                    type: "one-time",
                  },
                  bubbles: true,
                },
              );

              if (button && !bundlelink) {
                button.classList.add("secondary-button");
              }
              if (button) {
                button.querySelector("span").innerHTML =
                  button.getAttribute("data-msg-atc") || "Add to cart";
              }
              document.dispatchEvent(subscriptionChangeEvent);
              this.updateNonBundleOneTimePrice(detail);
            } else {
              subscriptionChangeEvent = new CustomEvent(
                "Recharge:Widget:SubscriptionChange",
                {
                  detail: {
                    priceEl: el
                      .closest(".widget__header")
                      ?.querySelector(".subscription-price"),
                    type: "subscription",
                    first_selling_plan: variantSellingPlans[0],
                  },
                  bubbles: true,
                },
              );

              if (button && !bundlelink) {
                button.classList.add("secondary-button");
              }
              if (button) {
                button.querySelector("span").innerHTML =
                  detail.data.widget_settings.subscribe_without_discount_message;
                button.removeAttribute("disabled");
              }
              document.dispatchEvent(subscriptionChangeEvent);
              this.updateNonBundleSubscribePrice(selectElement, detail);
            }

            document.dispatchEvent(subscriptionChangeEvent);

            if (this.data.product.bundle_product?.price_rule === "dynamic") {
              this.setBundleItems();
            }
          });
        });
        [
          ...template.querySelectorAll(
            '.widget_checkbox input[type="checkbox"]',
          ),
        ][this.selected_option].click();

        if (variantSellingPlans.length === 0) {
          template
            .querySelector(
              '.widget__subscription--onetime .widget_checkbox input[type="checkbox"]',
            )
            .click();
          template.firstElementChild.style.display = "none";
        }
      }

      priceElement.innerHTML = this.priceFormatter(
        this.getSubscribePrice(variantSellingPlans[0]),
      );

      selectElement.addEventListener("change", (event) => {
        let selected = event.target.value;
        let idx = event.target.selectedIndex;
        let sellingPlan = variantSellingPlans.find(
          (item) => Number(item.selling_plan_id) === Number(selected),
        );
        if (sellingPlan) {
          if (sellingPlan.plan.price_adjustments_value) {
            this.selected_plan_index = idx;

            let chbox = template.querySelector(
              '.widget__subscription--onetime .widget_checkbox input[type="checkbox"]',
            );
            if (chbox) {
              if (chbox.checked) {
                this.updateNonBundleSubscribePrice(selectElement, detail);
              }
            }

            if (this.data.product.bundle_product?.price_rule === "dynamic") {
              headingElement.innerHTML = `${detail.data.widget_settings.subscribe_message}`;

              if (headingElement.closest(".subscription-details")) {
                if (
                  !headingElement
                    .closest(".subscription-details")
                    .querySelector(".widget-perk__tool-wrapper")
                ) {
                  headingElement
                    .closest(".subscription-details")
                    .querySelector(".subscription-details .subtext")
                    .insertAdjacentHTML(
                      "afterend",
                      `              <div class="widget-perk__tool-wrapper">
                <div class="widget-perk__tooltip-title">Subscription Perks</div>
                <div class="widget-perk__tooltip-content">
                  <ul>
                    ${this.subscriptionPerks
                      .map((perk) => `<li>${perk.trim()}</li>`)
                      .join("")}
                  </ul>
                </div>
              </div>`,
                    );
                }
              }
              this.setBundleItems();
            } else {
              headingElement.innerHTML = `${detail.data.widget_settings.subscribe_message}`;
              if (headingElement.closest(".subscription-details")) {
                if (
                  !headingElement
                    .closest(".subscription-details")
                    .querySelector(".widget-perk__tool-wrapper")
                ) {
                  headingElement
                    .closest(".subscription-details")
                    .querySelector(".subscription-details .subtext")
                    .insertAdjacentHTML(
                      "afterend",
                      `              <div class="widget-perk__tool-wrapper">
                <div class="widget-perk__tooltip-title">Subscription Perks</div>
                <div class="widget-perk__tooltip-content">
                  <ul>
                    ${this.subscriptionPerks
                      .map((perk) => `<li>${perk.trim()}</li>`)
                      .join("")}
                  </ul>
                </div>
              </div>`,
                    );
                }
              }

              priceElement.innerHTML = this.priceFormatter(
                this.getSubscribePrice(sellingPlan),
              );
            }
          }

          if (!this.isSyncingFrequency) {
            this.syncProductOptionFromSellingPlan(sellingPlan);
          }
        }
        const subscriptionIsSelected = event.target
          .closest(".widget__wrapper")
          .querySelector("input#subscription_checkbox").checked;
        document.dispatchEvent(
          new CustomEvent("Recharge:Widget:DeliveryFrequency", {
            detail: {
              selling_plan: event.target.value,
              is_subscription: subscriptionIsSelected,
            },
          }),
        );
      });

      if (this.selected_plan_index >= selectElement.options.length) {
        this.selected_plan_index = 0;
      }
      if (this.selected_plan_index) {
        selectElement.selectedIndex = this.selected_plan_index;
      }

      /* Prefer the form's selected variant id — Recharge's detail.variant can
         lag behind Option radio changes on these club products. */
      const formVariantId = Number(
        this.form?.querySelector('input[name="id"]')?.value,
      );
      this.syncSellingPlanToVariant(selectElement, {
        id: formVariantId || detail.variant?.id,
        selling_plans: detail.variant?.selling_plans || [],
      });

      if (this.querySelector(".widget__wrapper")) {
        this.querySelector(".widget__wrapper").remove();
      }

      this.appendChild(template);

      if (this && template) {
        // this.initSubscriptionPerkExperiment(template)
      }

      this.initSubscriptionPerkExperiment(template);
      this.isSyncingFrequency = true;
      selectElement.dispatchEvent(new Event("change"));
      this.isSyncingFrequency = false;

      if (this.productForm) {
        this.productForm.style.visibility = "";
      }

      if (this.data.product.bundle_product?.price_rule === "fixed") {
        let priceContainer = document.querySelector('[id^="price-"]');
        if (priceContainer) {
          priceContainer.style.display = "";
          let priceSpan = priceContainer.querySelector("span.price-item");
          if (priceSpan) {
            priceSpan.innerHTML = this.priceFormatter(
              this.getLocalizedBasePrice(),
            );
          }
        }
      }

      // in template recharge-bundle-accordions deselect the 2 weeks and select 1 month
      if (this.closest("product-info").querySelector("cstmbundle-builder")) {
        if (this.querySelector(".custom-select.delivery-frequency-dropdown")) {
          const rechargeDropdown = this.querySelector(
            ".custom-select.delivery-frequency-dropdown select",
          );
          const selectedOption =
            rechargeDropdown.options[rechargeDropdown.selectedIndex];
          if (selectedOption.value === "9320988954") {
            rechargeDropdown.value = "9321054490";
          }
        }
      }
      this.setAttribute("data-loading", false);
    }

    getShopifyVariantData() {
      const scripts = document.querySelectorAll(
        "variant-radios [type='application/json'], variant-selects [type='application/json']",
      );

      for (const script of scripts) {
        try {
          const variants = JSON.parse(script.textContent);
          if (Array.isArray(variants)) return variants;
        } catch (_error) {
          /* try next script */
        }
      }

      return [];
    }

    shopifyVariantSellingPlanIds(variantId) {
      if (!variantId) return [];

      const match = this.getShopifyVariantData().find(
        (item) => Number(item.id) === Number(variantId),
      );
      if (!match) return [];

      return (match.selling_plan_allocations || [])
        .map((allocation) => Number(allocation.selling_plan_id))
        .filter((planId) => Number.isFinite(planId));
    }

    sellingPlansForVariant(variant) {
      const sellingPlans = variant?.selling_plans || [];
      if (!sellingPlans.length) return [];

      const variantId = Number(variant.id || variant.external_variant_id);
      const allowedPlanIds = this.shopifyVariantSellingPlanIds(variantId);
      if (!allowedPlanIds.length) return sellingPlans;

      const filtered = sellingPlans.filter((item) =>
        allowedPlanIds.includes(Number(item.selling_plan_id)),
      );
      return filtered.length ? filtered : sellingPlans;
    }

    frequencyKey(label) {
      const text = String(label || "").toLowerCase();
      if (/every\s*3\s*months?/.test(text)) return "3";
      if (/every\s*2\s*months?/.test(text)) return "2";
      if (/every\s*months?/.test(text)) return "1";
      return null;
    }

    syncProductOptionFromSellingPlan(sellingPlan) {
      const planName = sellingPlan?.plan?.selling_plan_name;
      const planKey = this.frequencyKey(planName);
      if (!planKey) return;

      const optionFieldset =
        document.querySelector(
          'variant-radios fieldset.product-form__input[data-option="Option"]',
        ) ||
        document.querySelector("variant-radios fieldset.product-form__input");
      if (!optionFieldset) return;

      const match = [...optionFieldset.querySelectorAll('input[type="radio"]')].find(
        (input) => this.frequencyKey(input.value) === planKey,
      );
      if (!match || match.checked) return;

      this.isSyncingFrequency = true;
      const label = optionFieldset.querySelector(`label[for="${match.id}"]`);
      (label || match).click();
      setTimeout(() => {
        this.isSyncingFrequency = false;
      }, 0);
    }

    findVariantIdForSellingPlan(planId) {
      const variants = this.getShopifyVariantData();
      if (!variants.length || !planId) return null;

      const selectedByPosition = {};
      document
        .querySelectorAll("variant-radios fieldset.product-form__input")
        .forEach((fieldset, index) => {
          const checked = fieldset.querySelector('input[type="radio"]:checked');
          if (checked) selectedByPosition[index + 1] = checked.value;
        });

      const candidates = variants.filter((variant) =>
        (variant.selling_plan_allocations || []).some(
          (allocation) =>
            Number(allocation.selling_plan_id) === Number(planId),
        ),
      );
      if (!candidates.length) return null;

      const match =
        candidates.find(
          (variant) =>
            (!selectedByPosition[2] ||
              variant.option2 === selectedByPosition[2]) &&
            (!selectedByPosition[3] ||
              variant.option3 === selectedByPosition[3]),
        ) || candidates[0];

      return match?.id || null;
    }

    resolveSubscriptionLineItem(selectElement) {
      /* Option radios / form variant id win. The Recharge dropdown can stay on
         a stale plan after changing "1 Pair Every 2 Months" in the form; Shopify
         rejects that mismatch and the add appears to do nothing. */
      const formVariantId = Number(
        this.form?.querySelector('input[name="id"]')?.value ||
          this.variant?.id ||
          this.variant?.external_variant_id,
      );
      const allowedPlanIds = this.shopifyVariantSellingPlanIds(formVariantId);
      let sellingPlanId = Number(selectElement?.value);

      if (allowedPlanIds.length) {
        if (!allowedPlanIds.includes(sellingPlanId)) {
          sellingPlanId = allowedPlanIds[0];
          if (selectElement) {
            this.isSyncingFrequency = true;
            selectElement.value = String(sellingPlanId);
            this.selected_plan_index = selectElement.selectedIndex;
            this.isSyncingFrequency = false;
          }
        }

        return {
          id: formVariantId,
          selling_plan: sellingPlanId,
        };
      }

      return {
        id: formVariantId,
        selling_plan: Number.isFinite(sellingPlanId) ? sellingPlanId : null,
      };
    }

    syncSellingPlanToVariant(selectElement, variant) {
      if (!selectElement || !variant) return;

      const sellingPlans = this.sellingPlansForVariant(variant);
      if (!sellingPlans.length) return;

      const allowedPlanIds = sellingPlans.map((item) =>
        Number(item.selling_plan_id),
      );
      const selectedPlanId = Number(selectElement.value);
      if (allowedPlanIds.includes(selectedPlanId)) return;

      const matchingOption = [...selectElement.options].find((option) =>
        allowedPlanIds.includes(Number(option.value)),
      );
      if (!matchingOption) return;

      this.isSyncingFrequency = true;
      selectElement.value = matchingOption.value;
      this.selected_plan_index = selectElement.selectedIndex;
      this.isSyncingFrequency = false;
    }

    isOneTimeOnlyProduct() {
      return (
        this.data.product.bundle_product === null &&
        this.data.product.subscription_options.storefront_purchase_options ===
          "onetime_only"
      );
    }

    updatePriceContainer(priceContainer) {
      if (!priceContainer) {
        return;
      }

      if (!priceContainer.hasAttribute("data-price-mutation-watcher")) {
        priceContainer;
      }

      let nElement = priceContainer.cloneNode(true);

      nElement.removeAttribute("data-price-mutation-watcher");

      priceContainer.replaceWith(nElement);

      this.startPriceObserver();

      return nElement;
    }

    updateNonBundleOneTimePrice(detail) {
      if (this.data.product.bundle_product) {
        return;
      }

      let priceContainer = document.querySelector('[id^="price-"]');
      if (!priceContainer) {
        return;
      }

      priceContainer = this.updatePriceContainer(priceContainer);

      priceContainer.style.display = "";
      let priceSpan = priceContainer.querySelector("span.price-item");
      if (!priceSpan) {
        return;
      }

      const localizedPrice = this.getLocalizedBasePrice();
      priceSpan.innerHTML = this.priceFormatter(localizedPrice);
      priceSpan.setAttribute(
        "data-currency-price",
        String(this.toCents(localizedPrice)),
      );
      this.formatMainPdpPriceFromDataAttr();
    }

    updateNonBundleSubscribePrice(selectElement, detail) {
      if (this.data.product.bundle_product) {
        return;
      }

      let selected = selectElement.value;
      let sellingPlan = this.sellingPlansForVariant(detail.variant).find(
        (item) => Number(item.selling_plan_id) === Number(selected),
      );

      if (!sellingPlan || !sellingPlan.plan.price_adjustments_value) {
        return;
      }

      let priceContainer = document.querySelector('[id^="price-"]');
      if (!priceContainer) {
        return;
      }

      priceContainer = this.updatePriceContainer(priceContainer);

      priceContainer.style.display = "";
      let priceSpan = priceContainer.querySelector("span.price-item");
      if (!priceSpan) {
        return;
      }

      const localizedPrice = this.getSubscribePrice(sellingPlan);
      priceSpan.innerHTML = this.priceFormatter(localizedPrice);
      priceSpan.setAttribute(
        "data-currency-price",
        String(this.toCents(localizedPrice)),
      );
      this.formatMainPdpPriceFromDataAttr();
    }

    /**
     * Sets the bundle items and updates the prices.
     *
     * @param {Object} event - The event object.
     */
    async setBundleItems(event) {
      // Get the items from the event detail or use the current bundle items
      let items = event?.detail?.items || this.bundle_items;
      this.bundle_items = items || [];

      // If the product is a dynamic bundle
      if (this.data.product.bundle_product?.price_rule === "dynamic") {
        // Get the selected selling plan ID
        let sellingPlanSelect = this.querySelector(
          ".delivery-frequency-dropdown select",
        )?.value;
        let selectPlan = null;

        // If a selling plan ID is selected
        if (Number(sellingPlanSelect)) {
          // Find the selling plan group that contains the selected selling plan
          let splan = this.data.product.selling_plan_groups.find((spg) => {
            return spg.selling_plans.find(
              (sp) => Number(sp.selling_plan_id) === Number(sellingPlanSelect),
            );
          });

          // If a selling plan group is found, find the selected selling plan
          if (splan) {
            selectPlan = splan.selling_plans.find(
              (sp) => Number(sp.selling_plan_id) === Number(sellingPlanSelect),
            );
          }
        }

        let discountPercentage = this.getDiscountPercentage(this.bundle_items);

        // Build the bundle parameters with and without the selected selling plan
        let withSellingPlan = await this.dynamicBundleParamBuilder(
          Number(this.variant.id),
          this.bundle_items,
          selectPlan || null,
          true,
        );
        let noSellingPlan = await this.dynamicBundleParamBuilder(
          Number(this.variant.id),
          this.bundle_items,
          null,
          true,
        );

        let totalComputationWithoutDiscount = (acc, item) => {
          return acc + item.computed_price;
        };

        let totalComputationWithDiscount = (acc, item) => {
          return (
            acc +
            (item.computed_price -
              item.computed_price * (discountPercentage / 100))
          );
        };

        // Calculate the total prices with and without the selected selling plan
        let totalWithSellingPlan = withSellingPlan.selections.reduce(
          totalComputationWithoutDiscount,
          0,
        );
        let totalWithoutSellingPlan = noSellingPlan.selections.reduce(
          totalComputationWithoutDiscount,
          0,
        );

        // Calculate the total prices with and without the selected selling plan
        let totalWithSellingPlanWithDiscount =
          withSellingPlan.selections.reduce(totalComputationWithDiscount, 0);
        let totalWithoutSellingPlanWithDiscount =
          noSellingPlan.selections.reduce(totalComputationWithDiscount, 0);

        // Update the subscription price with the total price with the selected selling plan
        let subscriptionPrice = this.querySelector(
          ".widget__container .subscription-price",
        );
        if (subscriptionPrice) {
          if (discountPercentage) {
            subscriptionPrice.innerHTML = `${this.priceFormatter(
              totalWithSellingPlanWithDiscount || 0,
            )} <s>${this.priceFormatter(totalWithoutSellingPlan)}</s>`;
          } else {
            if (totalWithSellingPlanWithDiscount === totalWithoutSellingPlan) {
              subscriptionPrice.innerHTML = this.priceFormatter(
                totalWithSellingPlan || 0,
              );
            } else {
              subscriptionPrice.innerHTML = `${this.priceFormatter(
                totalWithSellingPlanWithDiscount || 0,
              )} <s>${this.priceFormatter(totalWithoutSellingPlan)}</s>`;
            }
          }
        }

        // Update the one-time subscription price with the total price without the selected selling plan
        let onetimeSubscriptionPrice = this.querySelector(
          ".widget__subscription--onetime .subscription-price",
        );
        if (onetimeSubscriptionPrice) {
          if (discountPercentage) {
            onetimeSubscriptionPrice.innerHTML = `${this.priceFormatter(
              totalWithoutSellingPlanWithDiscount || 0,
            )} <s>${this.priceFormatter(totalWithoutSellingPlan)}</s>`;
          } else {
            onetimeSubscriptionPrice.innerHTML = this.priceFormatter(
              totalWithoutSellingPlan || 0,
            );
          }
        }

        // Update the price container with the total price without the selected selling plan
        let priceContainer = document.querySelector('[id^="price-"]');
        if (priceContainer) {
          priceContainer = this.updatePriceContainer(priceContainer);
          let priceSpan = priceContainer.querySelector("span.price-item");
          if (priceSpan) {
            let oneTimeCheckBox = this.querySelector(
              '.widget__subscription--onetime .widget_checkbox input[type="checkbox"]',
            );

            priceContainer.style.display = "";

            if (discountPercentage) {
              let selectedTotalPrice = oneTimeCheckBox?.checked
                ? totalWithoutSellingPlan
                : totalWithSellingPlan;
              let selectedTotalPriceWithDiscount = oneTimeCheckBox?.checked
                ? totalWithoutSellingPlanWithDiscount
                : totalWithSellingPlanWithDiscount;
              let displayPrices = `${this.priceFormatter(
                selectedTotalPriceWithDiscount || 0,
              )} <s>${this.priceFormatter(totalWithoutSellingPlan || 0)}</s>`;
              priceSpan.innerHTML =
                selectedTotalPrice <= 0 ? "Bundle & Save" : displayPrices;
              priceSpan.setAttribute(
                "data-currency-price",
                parseFloat(selectedTotalPriceWithDiscount) * 100,
              );
              if (priceSpan.querySelector("s")) {
                priceSpan
                  .querySelector("s")
                  .setAttribute(
                    "data-currency-price",
                    selectedTotalPrice <= 0 || selectedTotalPrice == ""
                      ? "Bundle & Save"
                      : parseFloat(selectedTotalPrice) * 100,
                  );
              }
            } else {
              let selectedTotalPrice = oneTimeCheckBox?.checked
                ? totalWithoutSellingPlan
                : totalWithSellingPlan;
              if (!oneTimeCheckBox?.checked) {
                let selectedTotalPriceWithDiscount = oneTimeCheckBox?.checked
                  ? totalWithoutSellingPlanWithDiscount
                  : totalWithSellingPlanWithDiscount;
                let displayPrices;
                if (
                  selectedTotalPriceWithDiscount === totalWithoutSellingPlan
                ) {
                  displayPrices = `${this.priceFormatter(
                    selectedTotalPriceWithDiscount || 0,
                  )}`;
                } else {
                  displayPrices = `${this.priceFormatter(
                    selectedTotalPriceWithDiscount || 0,
                  )} <s>${this.priceFormatter(
                    totalWithoutSellingPlan || 0,
                  )}</s>`;
                }
                priceSpan.innerHTML =
                  selectedTotalPrice <= 0 ? "Bundle & Save" : displayPrices;
                priceSpan.setAttribute(
                  "data-currency-price",
                  parseFloat(selectedTotalPriceWithDiscount) * 100,
                );
              } else {
                priceSpan.innerHTML =
                  selectedTotalPrice <= 0
                    ? "Bundle & Save"
                    : this.priceFormatter(selectedTotalPrice || 0);
                priceSpan.setAttribute(
                  "data-currency-price",
                  selectedTotalPrice <= 0 || selectedTotalPrice == ""
                    ? "Bundle & Save"
                    : parseFloat(selectedTotalPrice) * 100,
                );
              }
            }
          }
        }
      }
    }

    initSubscriptionPerkExperiment(template) {
      const tooltip = this.querySelector("[data-subscription-tooltip]");

      if (!tooltip) {
        return;
      }

      const tooltipToggle = tooltip.querySelector(
        "[data-subscription-tooltip-toggle]",
      );

      if (!tooltipToggle) {
        return;
      }

      const tooltipWrapper = this.querySelector(
        "[data-subscription-tooltip-wrapper]",
      );

      const handleScroll = () => {
        tooltipToggle.classList.remove("is-active");
        window.removeEventListener("scroll", handleScroll);
      };

      const setupTooltipEvents = () => {
        tooltipToggle.removeEventListener("mouseenter", handleMouseEnter);
        tooltipToggle.removeEventListener("mouseleave", handleMouseLeave);
        tooltipToggle.removeEventListener("click", handleClick);
        document.removeEventListener("click", handleClickOutside);

        if (window.matchMedia("(min-width: 750px)").matches) {
          tooltipToggle.addEventListener("mouseenter", handleMouseEnter);
          tooltipToggle.addEventListener("mouseleave", handleMouseLeave);
        } else {
          tooltipToggle.addEventListener("click", handleClick);
          document.addEventListener("click", handleClickOutside);
        }
      };

      const handleMouseEnter = () => {
        tooltipToggle.classList.add("is-active");

        gtag("event", "subscription_benefits_view");

        window.addEventListener("scroll", handleScroll);
      };

      const handleMouseLeave = () => {
        tooltipToggle.classList.remove("is-active");
        window.removeEventListener("scroll", handleScroll);
      };

      const handleClick = (event) => {
        const isActive = tooltipToggle.classList.toggle("is-active");
        if (isActive) {
          gtag("event", "subscription_benefits_view");

          window.addEventListener("scroll", handleScroll);
        } else {
          window.removeEventListener("scroll", handleScroll);
        }
      };

      const handleClickOutside = (event) => {
        if (!tooltipWrapper.contains(event.target)) {
          tooltipToggle.classList.remove("is-active");
          window.removeEventListener("scroll", handleScroll);
        }
      };
      tooltipWrapper.style.display = "flex";
      const throttledSetupTooltipEvents = throttle(setupTooltipEvents, 200);

      setupTooltipEvents();

      window.addEventListener("resize", throttledSetupTooltipEvents);
    }

    /**
     * Function to set the loading state.
     * It disables the submit button, adds a loading class to it, and shows the loading spinner.
     */
    setLoading() {
      // Get the submit button
      let submitButton = this.productForm.querySelector('[type="submit"]');

      // Disable the submit button
      submitButton.setAttribute("aria-disabled", true);

      // Add the loading class to the submit button
      submitButton.classList.add("loading");

      // Show the loading spinner
      this.productForm
        .querySelector(".loading-overlay__spinner")
        .classList.remove("hidden");
    }

    /**
     * Function to unset the loading state.
     * It enables the submit button, removes the loading class from it, and hides the loading spinner.
     * It also removes the 'is-empty' class from the cart if it exists and dispatches a 'Cart:Drawer:Changed' event.
     */
    unsetLoading() {
      // Get the submit button
      let submitButton = this.productForm.querySelector('[type="submit"]');

      // Remove the loading class from the submit button
      submitButton.classList.remove("loading");

      // If the cart exists and it has the 'is-empty' class, remove the 'is-empty' class
      if (this.cart && this.cart.classList.contains("is-empty")) {
        this.cart.classList.remove("is-empty");
      }

      // Enable the submit button
      submitButton.removeAttribute("aria-disabled");

      // Hide the loading spinner
      this.productForm
        .querySelector(".loading-overlay__spinner")
        .classList.add("hidden");

      // Dispatch a 'Cart:Drawer:Changed' event
      document.dispatchEvent(new CustomEvent("Cart:Drawer:Changed"));
    }

    /**
     * Function to handle the form submission event.
     *
     * @param {Event} event - The form submission event.
     * @returns {Promise} A promise that resolves with the result of the form submission.
     */
    async submit(event) {
      // Prevent the default form submission behavior
      event.stopImmediatePropagation();
      event.stopPropagation();
      event.preventDefault();
      const quantityParent = document.querySelector(".product-form__quantity");

      if (this.isOneTimeOnlyProduct()) {
        return false;
      }

      // Initialize the item and items array
      let item = {
        id: this.variant.id,
        quantity: this.quantity,
      };
      let items = [];

      let isSubscription = false;

      // Set the loading state
      this.setLoading();

      // Check if the product is a bundle product
      if (this.data.product.bundle_product) {
        // If there are no bundle items, unset the loading state and return
        if (!this.bundle_items.length) {
          this.unsetLoading();
          return false;
        }

        // Get the selected selling plan
        let sellingPlanSelect = this.querySelector(
          ".delivery-frequency-dropdown select",
        ).value;
        const subscriptionCheckboxChecked = this.querySelector(
          ".check-container #subscription_checkbox",
        ).checked;
        if (sellingPlanSelect && subscriptionCheckboxChecked) {
          isSubscription = true;
        }

        // Validate the bundle
        let valid = await this.validateBundle(
          Number(this.variant.id),
          this.bundle_items,
        );
        if (valid) {
          // If the bundle price rule is 'fixed', build the parameters for a fixed bundle
          if (this.data.product.bundle_product?.price_rule === "fixed") {
            let params = await this.fixedBundleParamBuilder(
              Number(this.variant.id),
              this.bundle_items,
            );
            let rbId = await window.recharge.bundle.getBundleId(params);
            let contents = this.bundle_items.map(
              (i) => `${i.quantity}x ${i.title} - ${i.variant_title}`,
            );

            // Set the properties of the item
            item.properties = {
              Contents: contents.join(", "),
              _rb_id: rbId,
            };

            // Set the selling plan of the item if it has selling plans
            if (this.variant.selling_plans.length > 0) {
              item.selling_plan = Number(sellingPlanSelect);
            }
          }

          // If the bundle price rule is 'dynamic', build the parameters for a dynamic bundle
          if (this.data.product.bundle_product?.price_rule === "dynamic") {
            let oneTimeCheckBox = this.querySelector(
              '.widget__subscription--onetime .widget_checkbox input[type="checkbox"]',
            );
            let selectPlan = null;

            // If a selling plan is selected and the one-time checkbox is not checked, find the selected selling plan
            if (Number(sellingPlanSelect) && !oneTimeCheckBox?.checked) {
              let splan = this.data.product.selling_plan_groups.find((spg) => {
                return spg.selling_plans.find(
                  (sp) =>
                    Number(sp.selling_plan_id) === Number(sellingPlanSelect),
                );
              });
              if (splan) {
                selectPlan = splan.selling_plans.find(
                  (sp) =>
                    Number(sp.selling_plan_id) === Number(sellingPlanSelect),
                );
              }
            }

            // Build the parameters for a dynamic bundle
            let params = await this.dynamicBundleParamBuilder(
              Number(this.variant.id),
              this.bundle_items,
              selectPlan || null,
            );

            // Get the items for the dynamic bundle
            let cartdata = await window.recharge.bundle.getDynamicBundleItems(
              params,
              this.product_handle,
            );

            // Set the items array
            items = [...cartdata];
          }
        } else {
          // If the bundle is not valid, unset the loading state and return
          this.unsetLoading();
          return false;
        }
      } else {
        // If the product is not a bundle product, set the item quantity and selling plan
        let quantityElement = quantityParent.querySelector(
          "input.quantity__input",
        );

        if (quantityElement) {
          item.quantity = Number(quantityElement.value);
        }

        /* Use the tracked selection index (kept in sync on every checkbox
           click, including the auto-click on re-render) rather than
           re-reading the checkbox's .checked state here, which was
           observed to be stale relative to the user's last click and
           caused one-time selections to submit with a selling_plan. */
        const isOneTimeSelected = this.selected_option === 1;
        const selectElement = this.querySelector(
          ".delivery-frequency-dropdown select",
        );

        if (isOneTimeSelected) {
          isSubscription = false;

          item.selling_plan = null;
        } else if (
          this.data.product.subscription_options.storefront_purchase_options ===
          "subscription_only"
        ) {
          isSubscription = true;

          const resolved = this.resolveSubscriptionLineItem(selectElement);
          item.id = resolved.id;
          item.selling_plan = resolved.selling_plan;
        } else if (
          this.data.product.subscription_options.storefront_purchase_options ===
          "subscription_and_onetime"
        ) {
          isSubscription = true;

          const resolved = this.resolveSubscriptionLineItem(selectElement);
          item.id = resolved.id;
          item.selling_plan = resolved.selling_plan;
          if (isOneTimeSelected) {
            item.selling_plan = null;
          }
        }
      }

      // Send a request to add the items to the cart
      let result = await this.bundleATCRequest(
        items.length ? items : [item],
        isSubscription,
      );

      // Reset quantity field
      if (!this.data.product.bundle_product) {
        quantityParent.querySelector("input.quantity__input").value = 1;
      }

      // Unset the loading state
      this.unsetLoading();

      // Update the cart and render its contents after a delay
      setTimeout(() => {
        if (result?.status) return;

        publish(PUB_SUB_EVENTS.cartUpdate, { source: "product-form" });
        this.cart.renderContents(result, true);
        if (result.items?.length) {
          document.querySelector("cart-drawer").classList.remove("is-empty");
        }
      }, 300);

      return false;
    }

    /**
     * Function to validate a bundle based on its price rule.
     *
     * @param {number} bundleVariant - The variant ID of the bundle.
     * @param {Array} selections - The array of selected items in the bundle.
     * @returns {Promise} A promise that resolves with the validation result.
     * @throws {Error} Throws an error if the bundle price rule is invalid.
     */
    async validateBundle(bundleVariant, selections) {
      // Check if the bundle price rule is 'fixed'
      if (this.data.product.bundle_product?.price_rule === "fixed") {
        // Build the parameters for a fixed bundle
        let params = await this.fixedBundleParamBuilder(
          bundleVariant,
          selections,
        );
        // Validate the bundle
        let isValid = await window.recharge.bundle.validateBundle(params);
        // Return the validation result
        return isValid;
      }
      // Check if the bundle price rule is 'dynamic'
      if (this.data.product.bundle_product?.price_rule === "dynamic") {
        // Build the parameters for a dynamic bundle
        let params = await this.dynamicBundleParamBuilder(
          bundleVariant,
          selections,
        );
        // Validate the dynamic bundle
        let isValid =
          await window.recharge.bundle.validateDynamicBundle(params);
        // Return the validation result
        return isValid;
      }
      // Throw an error if the bundle price rule is invalid
      throw new Error("Invalid bundle price rule");
    }

    /**
     * Builds the parameters for a dynamic bundle.
     *
     * @param {number} bundleVariant - The variant ID of the bundle.
     * @param {Array} selections - The selected items in the bundle.
     * @param {Object} sellingPlan - The selling plan for the bundle.
     * @package {boolean} compute - Whether to compute the bundle price.
     * @returns {Object} The parameters for the dynamic bundle.
     */
    async dynamicBundleParamBuilder(
      bundleVariant,
      selections,
      sellingPlan = null,
      compute = false,
    ) {
      // Map the selections to an array of item objects
      let items = selections.map((s) => {
        // Create an item object with the collection ID, product ID, variant ID, and quantity
        let itemObject = {
          collectionId: s.collection_id,
          externalProductId: s.product_id,
          externalVariantId: s.variant_id,
          quantity: s.quantity,
        };

        if (compute) {
          itemObject.computed_price = s.quantity * s.price;
        }

        // If a selling plan is provided (skip per-line subscription adjustment for one-time add-ons)
        if (sellingPlan && !s.subscription_price_exempt) {
          // Find a selling plan group that matches the selling plan's order interval frequency and unit type
          let plan = s.selling_plan_groups.find((sp) => {
            const byAttributes = sp.name
              .toLowerCase()
              .includes(
                `${sellingPlan.order_interval_frequency} ${sellingPlan.order_interval_unit_type}`,
              );
            if (!byAttributes) {
              const byName =
                sp.name.toLowerCase() ===
                sellingPlan.selling_plan_name.toLowerCase();
              if (!byName && sellingPlan.order_interval_frequency === 1) {
                // there's no number on the title but the frequency is 1
                // example scenario: every week / every month
                const byFrequencyOnly = sp.name
                  .toLowerCase()
                  .includes(`every ${sellingPlan.order_interval_unit_type}`);
                if (byFrequencyOnly) {
                  return byFrequencyOnly;
                }
              }
              return byName;
            }
            return byAttributes;
          });

          // If a matching selling plan group is found
          if (plan) {
            // Find a selling plan in the group that matches the selling plan's name
            let sellingPlanOption = plan.selling_plans.find((sp) => sp);
            // If a matching selling plan is found, add the selling plan ID to the item object
            if (sellingPlanOption) {
              itemObject.sellingPlan = Number(sellingPlanOption.id);
              if (compute) {
                let adjustment = sellingPlanOption.price_adjustments.find(
                  (pa) => pa,
                );
                itemObject.computed_price =
                  s.quantity * (s.price - s.price * (adjustment.value / 100));
              }
            }
          }
        }

        // Return the item object
        return itemObject;
      });

      // Return an object with the external product ID, external variant ID, and the array of item objects
      return {
        externalProductId: Number(this.data.product.external_product_id),
        externalVariantId: bundleVariant,
        selections: items,
      };
    }

    /**
     * Function to build the parameters for a fixed bundle.
     *
     * @param {number} bundleVariant - The variant ID of the bundle.
     * @param {Array} selections - The array of selected items in the bundle.
     * @returns {Promise} A promise that resolves with the bundle parameters.
     */
    async fixedBundleParamBuilder(bundleVariant, selections) {
      return {
        // The external product ID
        externalProductId: Number(this.data.product.external_product_id),
        // The variant ID of the bundle
        externalVariantId: bundleVariant,
        // The array of selected items in the bundle
        selections:
          selections.map((s) => {
            return {
              // The collection ID of the item
              collectionId: s.collection_id,
              // The external product ID of the item
              externalProductId: s.product_id,
              // The variant ID of the item
              externalVariantId: s.variant_id,
              // The quantity of the item
              quantity: s.quantity,
            };
          }) || [],
      };
    }

    /**
     * Function to send a POST request to add items to the cart.
     *
     * @param {Array} items - The array of items to add to the cart.
     * @param {Boolean} isSubscription - Indicates whether to add a `has_subscription` attribute to the cart body.
     * @returns {Promise} A promise that resolves with the updated cart data.
     */
    bundleATCRequest(items, isSubscription) {
      return new Promise((resolve, _reject) => {
        // Prepare the request body
        // experiment gs-927
        let modifiedItems = items;
        if (
          window.location.href.includes(
            "55-november-experiment-value-addition-section-checkout-dm",
          )
        ) {
          try {
            modifiedItems = items.map((item) => {
              return {
                ...item,
                properties: {
                  ...item.properties,
                  _experiment_gs927: true,
                },
              };
            });
          } catch (e) {
            console.info("Error adding properties", e);
          }
        }
        // end experiment gs-927
        if (window.location.pathname.includes("-bundle")) {
          try {
            const bundleProps = {
              _bundle_page_path: window.location.pathname,
              _purchase_type: isSubscription ? "subscription" : "one_time",
            };
            modifiedItems = modifiedItems.map((item) => ({
              ...item,
              properties: { ...(item.properties || {}), ...bundleProps },
            }));
          } catch (e) {
            console.info("Error adding bundle properties", e);
          }
        }

        // Editing an existing bundle from the cart drawer's "Edit Bundle" link: force
        // the new items onto the bundle id being edited (instead of a fresh one Recharge
        // would otherwise generate) and drop the old cart lines for that id first, so the
        // cart ends up with one updated bundle instead of two.
        const editBundleId = new URLSearchParams(window.location.search).get(
          "edit_bundle_id",
        );
        if (editBundleId) {
          try {
            modifiedItems = modifiedItems.map((item) => {
              const properties = { ...(item.properties || {}) };
              const existingKey = BUNDLE_ID_PROPERTY_KEYS.find(
                (key) => key in properties,
              );
              properties[existingKey || BUNDLE_ID_PROPERTY_KEYS[0]] = editBundleId;
              return { ...item, properties };
            });
          } catch (e) {
            console.info("Error forcing edit bundle id onto properties", e);
          }
        }

        const body = JSON.stringify({
          items:
            /* experiment gs-927 - change back to items when cleaning up */ modifiedItems /* end experiment gs-927 */,
          sections: this.cart
            .getSectionsToRender()
            .map((section) => section.id)
            .join(","),
          ...(isSubscription && {
            attributes: {
              has_subscription: isSubscription,
            },
          }),
        });

        const addToCart = () =>
          // Send a POST request to add the items to the cart
          fetch("/cart/add.js", {
            body: body,
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              "X-Requested-With": "xmlhttprequest",
            },
            method: "POST",
          })
            .then((response) => {
              if (isSubscription) {
                gtag("event", "subscription_add_to_cart");
              }
              // Parse the response as JSON
              return response.json().then((cart) => {
                // Fetch the updated sections data
                return this.getSections().then((sections) => {
                  // Merge the cart and sections data into a single object
                  return Object.assign({}, cart, { sections });
                });
              });
            })
            .then((result) => {
              // Resolve the promise with the result
              return resolve(result);
            });

        if (editBundleId) {
          this.removeExistingBundleItems(editBundleId).then(addToCart);
        } else {
          addToCart();
        }
      });
    }

    /**
     * Removes every current cart line item belonging to the given bundle id (matched on
     * any of BUNDLE_ID_PROPERTY_KEYS), used as the last-resort "remove then re-add with
     * the same id" approach to editing a bundle already in the cart.
     *
     * @param {string} bundleId - The bundle id whose existing lines should be removed.
     * @returns {Promise} Resolves once all matching lines have been removed.
     */
    async removeExistingBundleItems(bundleId) {
      const fetchCart = () =>
        fetch("/cart.js", {
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
        }).then((response) => response.json());

      const findMatchingLines = (cart) =>
        cart.items.filter((line) => {
          const properties = line.properties || {};
          return BUNDLE_ID_PROPERTY_KEYS.some(
            (key) => properties[key] === bundleId,
          );
        });

      const removeLines = async (lines) => {
        for (const line of lines) {
          try {
            const response = await fetch("/cart/change.js", {
              body: JSON.stringify({ id: line.key, quantity: 0 }),
              credentials: "same-origin",
              headers: {
                "Content-Type": "application/json",
                "X-Requested-With": "xmlhttprequest",
              },
              method: "POST",
            });
            if (!response.ok) {
              console.info(
                "Failed to remove existing bundle line",
                line.key,
                response.status,
              );
            }
          } catch (e) {
            console.info("Error removing existing bundle line", line.key, e);
          }
        }
      };

      try {
        await removeLines(findMatchingLines(await fetchCart()));

        // A change request can fail without the fetch itself rejecting (a
        // non-ok response, or a request Shopify silently no-ops), which
        // previously left the old line in place to be merged with the
        // freshly-added one by cart/add.js. Re-check the live cart and
        // retry any lines that are still there before adding the new items.
        const remainingLines = findMatchingLines(await fetchCart());
        if (remainingLines.length > 0) {
          await removeLines(remainingLines);
        }
      } catch (e) {
        console.info("Error removing existing bundle items", e);
      }
    }

    /**
     * Function to fetch sections data from the server.
     *
     * @returns {Promise} A promise that resolves with the sections data.
     */
    getSections() {
      return new Promise((resolve, _reject) => {
        // Prepare the URL with the sections to render
        const url = `/?sections=${this.cart
          .getSectionsToRender()
          .map((section) => section.id)
          .join(",")}`;

        // Fetch the sections data from the server
        return fetch(url, {
          credentials: "same-origin",
          headers: {
            "Content-Type": "application/json",
            "X-Requested-With": "xmlhttprequest",
          },
        })
          .then(function (response) {
            // Parse the response as JSON
            return response.json();
          })
          .then(function (json) {
            // Resolve the promise with the JSON data
            return resolve(json);
          })
          .catch(function (err) {
            // Log any errors
            console.error(err);
          });
      });
    }
  }

  if (!window.customElements.get("recharge-widget")) {
    window.customElements.define("recharge-widget", RechargeWidget);
  }
})(window);
